<template>
  <CustomDropdown
    ref="dropdownRef"
    maxHeight="300px"
    gap="10px"
    position="start"
    dropdownCSS="p-0"
    v-if="!hideDropdownWrapper"
    @close="handleDropdownClose"
  >
    <template #trigger>
      <div class="secondary_small gap-1">
        <ThemeImage
          class="size-4"
          :icon="imageNames.CALL_GREY"
          alt="dropdown_icon"
        />
        Add to Dialer Campaign
        <ThemeImage
          class="size-4"
          :icon="imageNames.DROPDOWN_GREY"
          alt="dropdown_icon"
        />
      </div>
    </template>

    <template #default>
      <div class="flex flex-col">
        <div class="flex flex-col max-h-[244px]">
          <div class="flex flex-col py-3 px-2 border_bottom">
            <span
              class="secondary_color proxima_nova font-semibold text-xs leading-[17px] px-3 pb-2"
            >
              SELECT A DIALER CAMPAIGN
            </span>
            <CustomInput
              format="text"
              v-model="searchQuery"
              placeholder="Search here"
              type="primary"
              :icon="imageNames.SEARCH_VARIANT1"
              alt="search_icon"
              auto-focus
            />
          </div>
          <div
            v-if="filteredCampaigns.length"
            class="flex flex-col p-2 overflow-y-scroll custom_scrollbar"
          >
            <div
              v-for="(campaign, index) in filteredCampaigns"
              :key="`${campaign.id}_campaign_${index}`"
              class="dropdown_item flex-shrink-0 flex items-center flex-row gap-2 rounded-lg"
              @click="handleCampaignClick(campaign)"
            >
              <div class="flex flex-row items-center gap-1 text-sm">
                {{ campaign.name || "" }}
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-col p-2 border_top">
          <div
            class="dropdown_item flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
            @click="handleCreateDialerCampaign"
          >
            + Create New Dialer Campaign
          </div>
        </div>
      </div>
    </template>
  </CustomDropdown>
</template>

<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { debounce } from "lodash-es";
import { message } from "ant-design-vue";
import eventBus from "@/src/utils/eventBus.js";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import CallService from "@/src/api/CallService.js";
import store from "@/src/store/index.js";
import BroadcastService from "@/src/services/communications/broadcastService.js";

const router = useRouter();

const props = defineProps({
  selectedEntities: {
    type: Object,
    required: true,
  },
  source: { type: String, default: "" },
});

const emit = defineEmits([
  "success",
  "error",
  "entity-list-created",
  "add-to-dialer-campaign",
]);

const campaigns = ref([]);
const searchQuery = ref("");
const dropdownRef = ref(null);

const sessionId = ref(
  `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
);
const broadcastService = new BroadcastService(
  `campaign-channel-${sessionId.value}`,
  true,
);
const channelReadyHandler = ref(null);

const campaignDetails = computed(
  () => store.getters["callsCampaign/campaignDetails"],
);

const transformEntityForAPI = (entity) => ({
  entity_id: entity.id,
  entity_type: entity.entity_type,
});

const transformEntitiesForAPI = (entities) => {
  if (!entities || !Array.isArray(entities)) return [];
  return entities.map(transformEntityForAPI);
};

const filteredCampaigns = computed(() => {
  if (!searchQuery.value.trim()) return campaigns.value;
  return campaigns.value.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.value.trim().toLowerCase()),
  );
});

const fetchCampaigns = async () => {
  try {
    const statuses = ["draft", "paused"];

    const params = {
      page: 1,
      page_size: 50,
      search: searchQuery.value.trim() || undefined,
      status: statuses,
    };

    const response = await CallService.getAllCallCampaigns(params);
    if (response?.success) {
      const allCampaigns = response.campaigns || [];
      const currentCampaignId = campaignDetails.value?.id;

      campaigns.value = allCampaigns.filter(
        (campaign) => campaign.id !== currentCampaignId,
      );
    } else {
      campaigns.value = [];
    }
  } catch (error) {
    campaigns.value = [];
    emit("error", "Failed to fetch campaigns");
  }
};

const handleCampaignClick = async (campaign) => {
  const hasSelectedEntities =
    props.selectedEntities.select_all ||
    props.selectedEntities.selected_entities?.length > 0;

  if (!campaign?.id || !hasSelectedEntities) return;
  try {
    const response = await CallService.addEntitiesToCampaign({
      campaign_id: campaign.id,
      select_all: props.selectedEntities.select_all,
      entity_pairs: transformEntitiesForAPI(
        props.selectedEntities.selected_entities,
      ),
      unselected_entity_pairs: transformEntitiesForAPI(
        props.selectedEntities.unselected_entities,
      ),
    });
    if (response?.success) {
      emit("success", `Added to campaign: ${campaign.name}`);
      dropdownRef.value?.closeDropdown();
    } else {
      emit("error", response?.error || "Failed to add to campaign");
    }
  } catch (error) {
    console.error("Error adding to campaign:", error);
    emit("error", "Failed to add to campaign");
  }
};

const handleCreateDialerCampaign = async () => {
  try {
    const hasSelectedEntities =
      props.selectedEntities.selected_entities?.length > 0;

    if (!hasSelectedEntities) {
      throw new Error("No entities selected");
    }

    if (!campaignDetails.value?.name) {
      throw new Error("Current campaign details not found");
    }

    const newCampaignName = `${campaignDetails.value?.name}-2`;

    dropdownRef.value?.closeDropdown();

    const url = router.resolve({
      name: "sales-dialer-campaign-create",
      params: {
        campaignName: encodeURIComponent(newCampaignName),
      },
      query: {
        id: sessionId.value,
      },
    }).href;

    const entityDataToSend = {
      entityData: {
        select_all: props.selectedEntities.select_all,
        entity_pairs: transformEntitiesForAPI(
          props.selectedEntities.selected_entities,
        ),
        unselected_entity_pairs: transformEntitiesForAPI(
          props.selectedEntities.unselected_entities,
        ),
      },
      sourceCampaignName: campaignDetails.value?.name,
    };

    const handleChannelReady = (data) => {
      try {
        broadcastService.send("store-entity-data", entityDataToSend);
        emit("success", "new dialer campaign opened!");
      } catch (error) {
        console.error(
          "Failed to send entity data after ready confirmation:",
          error,
        );
        emit("error", "Failed to send data to new campaign page");
      }

      broadcastService.off("channel-ready", handleChannelReady);
      channelReadyHandler.value = null;
    };

    channelReadyHandler.value = handleChannelReady;
    broadcastService.listen("channel-ready", handleChannelReady);

    window.open(url, "_blank");
  } catch (error) {
    console.error("Error creating dialer campaign:", error);
    message.error(
      error.message || "An error occurred while creating the dialer campaign",
    );
  }
};

const cleanupBroadcastService = () => {
  if (channelReadyHandler.value) {
    try {
      broadcastService.off("channel-ready", channelReadyHandler.value);
    } catch (error) {
      console.error("Error cleaning up channel-ready handler:", error);
    }
  }

  try {
    broadcastService.destroy();
  } catch (error) {
    console.error("Error destroying broadcast service:", error);
  }
};

const handleDropdownClose = () => {
  searchQuery.value = "";
};

const debouncedFetchCampaigns = debounce(fetchCampaigns, 300);
watch(searchQuery, () => debouncedFetchCampaigns());

onMounted(() => {
  fetchCampaigns();
});

onUnmounted(() => {
  cleanupBroadcastService();
});
</script>

<style scoped>
.dropdown_item.selected {
  background-color: var(--white-opacity-8);
}
</style>
